'use client';

import { useState, useEffect } from 'react';
import PlayVideoIcon from 'components/icons/play-video';
import { withSuspense } from 'components/with-suspense';
import './video-block.css';

const VideoSkeleton = <div className="animate-pulse"></div>;

export const VideoEmbed = withSuspense(
    (props: {
        content: any;
        contentAutoplay: any;
        posterImage: string;
        posterImageAltText: string;
        posterImageVisibility: boolean;
        url: string;
        fetchPriority?: 'high' | 'low' | 'auto';
        lazy?: boolean;
    }) => {

        const getYouTubeId = () => {
            const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
            // Pulls YT ID from url entered into component on builder
            const match = props.url ? props.url.match(regExp) : '';
            return match ? match[7] : null;
        };

        const youTubeId = getYouTubeId()

        const posterImage = props.posterImage
            ? props.posterImage
            : youTubeId
                ? `https://i.ytimg.com/vi_webp/${youTubeId}/maxresdefault.webp`
                : null;

        const mobileImage = props.posterImage
            ? props.posterImage
            : youTubeId
                ? `https://i.ytimg.com/vi_webp/${youTubeId}/sddefault.webp`
                : null;

        const srcSet = props.posterImage
            ? null // No need for srcSet if props.posterImage exists
            : [
                mobileImage ? `${mobileImage} 1280w` : null,
                posterImage ? `${posterImage} 2560w` : null,
            ]
                .filter(Boolean)
                .join(', ');

        const posterAlt = props.posterImageAltText;
        const hidePoster = props.posterImageVisibility;
        const fetchPriority = props.fetchPriority ?? 'auto';
        const lazy = props.lazy ?? true;

        const [hasBeenClicked, setHasBeenClicked] = useState(false);
        const handlePosterClick = () => {
            setHasBeenClicked(true);
        };
        useEffect(() => {
            const links: { rel: string, href: string, crossOrigin?: string }[] = []

            if (youTubeId) {
                const ytDnsPrefetchLink = { rel: 'dns-prefetch', href: 'https://www.youtube.com' }
                links.push(ytDnsPrefetchLink)
            }

            if (!props.posterImage) {
                const ytPreConnectLink = { rel: 'preconnect', href: 'https://i.ytimg.com', crossOrigin: "anonymous" }
                links.push(ytPreConnectLink)
            }

            links.forEach(({ rel, href }) => {
                if (!document.querySelector(`link[href="${href}"]`)) {
                    const link = document.createElement('link')
                    link.rel = rel
                    link.href = href
                    document.head.appendChild(link)
                }
            })

        }, [youTubeId, props.posterImage])

        return (
            <div className="video-block">
                {posterImage && !hidePoster && (
                    <div className={`video-block-poster${hasBeenClicked ? ' video-is-active' : ''} ${
                        props.posterImage ? 'is-custom-image' : ''
                    }
          `}>
                        <img
                            loading={lazy ? 'lazy' : 'eager'}
                            fetchPriority={fetchPriority}
                            width="1280"
                            height="720"
                            src={posterImage}
                            srcSet={srcSet || undefined }
                            sizes="(min-width: 990px) 50vw, 100vw"
                            className="video-block-poster-image"
                            alt={posterAlt ? posterAlt : 'Video thumbnail image'}
                        />
                        <button
                            aria-label="Click image to play video"
                            className="video-block-poster-trigger"
                            onClick={handlePosterClick}
                        >
                            <PlayVideoIcon />
                        </button>
                    </div>
                )}
                {posterImage && !hidePoster && hasBeenClicked ? (
                    <div
                        className="video-block-builder-embed"
                        dangerouslySetInnerHTML={{ __html: props.contentAutoplay }}
                    />
                ) : posterImage && hidePoster ? (
                    <div
                        className="video-block-builder-embed no-poster"
                        dangerouslySetInnerHTML={{ __html: props.content }}
                    />
                ) : (
                    !posterImage && (
                        <div
                            className="video-block-builder-embed no-poster"
                            dangerouslySetInnerHTML={{ __html: props.content }}
                        />
                    )
                )}
            </div>
        );
    },
    VideoSkeleton
);